import ReactGA from 'react-ga4';
import { numify } from 'numify';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
/////////////////////////////////////////////////////////////
import { useCustomDispatch, useCustomSelector } from '@/core/redux/hooks';
import { telegramWebApp } from '@/main';
import { ExtraRewardResponse, Reward } from '@/core/interfaces';
import {
	Drawer,
	DrawerContent,
	DrawerClose,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	Button,
	Close,
	CoinImage,
	DrawerDescription,
	DiamondImage,
} from '@/app/shared/components';
import {
	setCounter,
	setDiamonds,
	setRewards,
	setStickerWonDialog,
	setTotalCoinsGenerated,
	setYoutubeRewards,
} from '@/core/redux/slices';
import { RewardService } from '@/core/services';

interface DailyRewardProps {
	isOpen: boolean;
	onClose: React.Dispatch<React.SetStateAction<boolean>>;
	reward: Reward;
}

export const TaskDetail: React.FC<DailyRewardProps> = ({
	isOpen,
	onClose,
	reward,
}: DailyRewardProps) => {
	/*********************
	 // #region Hooks
	**********************/
	const dispatch = useCustomDispatch();
	const { t } = useTranslation();

	/*********************
	 // #region Redux
	 **********************/
	const { rewards, youtubeRewards } = useCustomSelector(
		(state) => state.rewards
	);

	/*********************
     // #region Observable
    **********************/

	/*********************
 	// #region Functions
	**********************/
	const handleClick = (claim: boolean) => {
		telegramWebApp.HapticFeedback.impactOccurred('heavy');
		callClaimExtraReward(
			claim ? t('earn.rewardClaimed') : t('earn.rewardRequested'),
			(response: ExtraRewardResponse) => {
				const updatedRewards: Reward[] = [];
				if (reward.isYoutube) {
					youtubeRewards.map((element) => {
						if (element.id === reward.id) {
							updatedRewards.push({
								...element,
								availableForCollecting:
									response.data.availableForCollecting,
								completed: response.data.collected,
								requestedByUser: response.data.requestedByUser,
							});
						} else {
							updatedRewards.push(element);
						}
					});
				} else {
					rewards.map((element) => {
						if (element.id === reward.id) {
							updatedRewards.push({
								...element,
								availableForCollecting:
									response.data.availableForCollecting,
								completed: response.data.collected,
								requestedByUser: response.data.requestedByUser,
							});
						} else {
							updatedRewards.push(element);
						}
					});
				}

				dispatch(
					reward.isYoutube
						? setYoutubeRewards(updatedRewards)
						: setRewards(updatedRewards)
				);
				if (claim) {
					dispatch(setCounter(response.currentCoins));
					onClose(false);
				}
			}
		);
	};

	const handleGo = () => {
		!reward.requestedByUser && handleClick(false);
		reward.externalUrl && telegramWebApp.openLink(reward.externalUrl);
	};

	const callClaimExtraReward = async (
		successMessage: string,
		onSuccess: (data: ExtraRewardResponse) => void
	) =>
		toast.promise(RewardService.claimExtraReward(reward.id), {
			loading: t('general.loading'),
			success: (data: ExtraRewardResponse) => {
				onSuccess(data);
				// Emit event to Google Analytics
				ReactGA.event({
					category: 'rewards',
					action: `Claim ${reward.title}`,
				});

				// Check if has a gift
				if (data.isAwardWinning) {
					dispatch(setStickerWonDialog(true));
				}

				dispatch(setCounter(data.currentCoins));
				dispatch(setDiamonds(data.currentDiamonds));
				dispatch(setTotalCoinsGenerated(data.totalCoinsEarned));

				return successMessage;
			},
			error: t('earn.errorClaim'),
		});

	/*********************
	// #region Template
	**********************/
	return (
		<Drawer open={isOpen} onOpenChange={onClose}>
			<DrawerContent>
				<div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
				<div className="mx-auto w-full max-w-sm ">
					<DrawerHeader>
						<DrawerTitle>
							<VisuallyHidden>{reward.title}</VisuallyHidden>
							<DrawerClose className="right-5 text-right absolute">
								<Close fillcolor="#fff" />
							</DrawerClose>
						</DrawerTitle>
					</DrawerHeader>

					<DrawerDescription>
						<VisuallyHidden>{reward.title}</VisuallyHidden>
					</DrawerDescription>

					<div className="mx-auto px-8">
						<div className="flex flex-col my-4 justify-center items-center gap-5">
							<p className="px-5 text-2xl font-bold text-center justify-center">
								{reward.title}
							</p>
							<img
								src={
									reward.image ||
									process.env.VITE_PLACEHOLDER_ACTIVITY_IMAGE
								}
								alt="Reward"
								width={64}
								height={64}
							/>
						</div>

						{/* <div className="flex  gap-2 items-center"> */}
						{/* <CoinImage width={42} />
							<p className="font-bold text-3xl">
								{numify(reward.reward)}
							</p> */}
						<div className="flex justify-center items-center mb-6 gap-6">
							<div className="flex justify-start items-center gap-1">
								<CoinImage width={42} />
								<p className="pl-1 text-shadow font-bold text-3xl">
									+{numify(reward.reward)}
								</p>
							</div>
							{reward.diamondsReward > 0 && (
								<div className="flex justify-start items-center gap-1">
									<DiamondImage width={32} />
									<p className="pl-1 text-shadow font-bold text-3xl">
										+{numify(reward.diamondsReward)}
									</p>
								</div>
							)}
							{/* </div> */}
						</div>
						<p className="text-md text-center text-gray-300">
							{reward.requestedByUser &&
								!reward.availableForCollecting &&
								!reward.completed &&
								t('earn.waitCooldown')}
						</p>
					</div>

					<DrawerFooter>
						<DrawerClose asChild>
							<div className="flex flex-row items-center justify-between gap-4">
								<Button
									onClick={() => handleGo()}
									variant="secondary"
									className="py-6 w-full text-sm rounded-lg"
								>
									{t('general.go')}
								</Button>

								<Button
									variant="primary"
									className="py-6 w-full text-sm rounded-lg"
									disabled={
										(reward.requestedByUser &&
											!reward.availableForCollecting) ||
										reward.completed
									}
									onClick={() => {
										reward.requestedByUser
											? handleClick(true)
											: (() => {
													handleClick(false);
													reward.externalUrl &&
														telegramWebApp.openLink(
															reward.externalUrl
														);
											  })();
									}}
								>
									{!reward.requestedByUser
										? t('general.check')
										: reward.availableForCollecting
										? t('general.claim')
										: reward.requestedByUser &&
										  !reward.availableForCollecting &&
										  !reward.completed
										? t('general.checking')
										: reward.completed &&
										  t('general.completed')}
								</Button>
							</div>
						</DrawerClose>
					</DrawerFooter>
				</div>
			</DrawerContent>
		</Drawer>
	);
};
